import type { AnyFieldApi } from "@tanstack/react-form";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils/cn";

import { firstFieldError } from "./validators";

interface TextFieldProps {
  field: AnyFieldApi;
  label: string;
  type?: React.HTMLInputTypeAttribute;
  placeholder?: string;
  autoComplete?: string;
  description?: string;
  /** An error the server reported for this field (e.g. a 400 on save). The
   * field's own validators win: a value they reject is never sent. */
  serverError?: string;
  className?: string;
}

/**
 * Presentational text field bound to a TanStack Form field. Decoupled from the
 * form's data type (typed via AnyFieldApi) so it's reusable across every form.
 * Wires accessible label/error association and aria-invalid automatically.
 *
 *   <form.Field name="email" validators={{ onChange: zodField(schema) }}>
 *     {(field) => <TextField field={field} label={t("auth.email")} type="email" />}
 *   </form.Field>
 */
export function TextField({
  field,
  label,
  type = "text",
  placeholder,
  autoComplete,
  description,
  serverError,
  className,
}: TextFieldProps) {
  const error = firstFieldError(field) ?? serverError;
  const errorId = `${field.name}-error`;
  const descId = `${field.name}-desc`;
  const invalid = error !== undefined;

  return (
    <div className={cn("space-y-1.5", className)}>
      <Label htmlFor={field.name}>{label}</Label>
      <Input
        id={field.name}
        name={field.name}
        type={type}
        placeholder={placeholder}
        autoComplete={autoComplete}
        value={(field.state.value as string | undefined) ?? ""}
        onChange={(e) => field.handleChange(e.target.value)}
        onBlur={field.handleBlur}
        aria-invalid={invalid}
        aria-describedby={cn(description && descId, invalid && errorId) || undefined}
      />
      {description && (
        <p id={descId} className="text-sm text-muted-foreground">
          {description}
        </p>
      )}
      {invalid && (
        <p id={errorId} className="text-sm font-medium text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
